import React, { useEffect, useMemo, useState } from "react";
import {
  UserCheck,
  DollarSign,
  FileCheck,
  Wallet,
  Car,
  ShoppingBag,
  HandCoins,
  AlertTriangle,
  ArrowRight,
  Users2,
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  Building2,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardCard } from "./dashboard/DashboardCard";
import { UserList } from "./dashboard/UserList";
import { CollectedEMIs } from "./dashboard/CollectedEMIs";
import { ClosedHPs } from "./dashboard/ClosedHPs";
import { TotalLoanAmount } from "./dashboard/TotalLoanAmount";
import { AvailableVehicles } from "./dashboard/AvailableVehicles";
import { SoldVehicles } from "./dashboard/SoldVehicles";
import { SeizedHPs } from "./dashboard/SeizedHPs";
import DateRangeFilter from "../components/common/DateRangeFilter";
import { useDateRange, inRange, formatRangeLabel } from "../lib/dateFilter";
import { ExportMenu, type ExportRow } from "../lib/exportReports";
import { ActiveUsers } from "./dashboard/ActiveUsers";
import { HandLoansOverview } from "./dashboard/HandLoansOverview";
import {
  getFacilityCollectionSnapshot,
  useAdminEmiPayments,
  useUnifiedCustomers,
  readSeizedFinanceRecords,
  WORKSPACE_DATA_EVENT,
  type UnifiedCustomer,
} from "../lib/workspaceStore";
import { CONSULTANCY_DATA_EVENT, readConsultancyBundle } from "../lib/consultancyStore";
import { ACCOUNTING_DATA_EVENT } from "../lib/accountingStore";
import { computePaymentFlows, computeLiveCashBank } from "../lib/paymentFlows";
import {
  computeFinancePeriodMetrics,
  computePortfolioTotals,
  emiCashFromPayment,
  lateFeeCashFromPayment,
} from "../lib/portfolioMetrics";
import { useBooksOpening } from "../lib/booksOpeningStore";
import { useTranslation } from "../lib/i18n";
import { useChartTheme } from "../lib/chartTheme";

function formatInrCompact(n: number) {
  // Full amount always — layout shrinks text via card styles when needed.
  return `₹${Math.round(Math.max(0, n)).toLocaleString('en-IN')}`;
}

function countFullyClosedCustomers(customers: UnifiedCustomer[]): number {
  let n = 0;
  for (const c of customers) {
    if (!c.facilities.length) continue;
    if (c.facilities.every((f) => getFacilityCollectionSnapshot(f).isLedgerClosed)) n += 1;
  }
  return n;
}

const statsCardDefs = [
  { titleKey: "dashboard.totalUsers", icon: UserCheck, id: "total-users", accentTop: "border-l-[3px] border-l-zinc-400", iconColor: "text-zinc-600" },
  { titleKey: "dashboard.collectedEmis", icon: DollarSign, id: "collected-emis", accentTop: "border-l-[3px] border-l-emerald-600", iconColor: "text-emerald-700" },
  { titleKey: "dashboard.closedHps", icon: FileCheck, id: "closed-hps", accentTop: "border-l-[3px] border-l-teal-600", iconColor: "text-teal-700" },
  { titleKey: "dashboard.totalLoan", icon: Wallet, id: "total-loan", accentTop: "border-l-[3px] border-l-amber-500", iconColor: "text-amber-700" },
  { titleKey: "dashboard.vehiclesAvailable", icon: Car, id: "vehicles-available", accentTop: "border-l-[3px] border-l-zinc-400", iconColor: "text-zinc-600" },
  { titleKey: "dashboard.vehiclesSold", icon: ShoppingBag, id: "vehicles-sold", accentTop: "border-l-[3px] border-l-emerald-600", iconColor: "text-emerald-700" },
  { titleKey: "dashboard.handLoans", icon: HandCoins, id: "hand-loans", accentTop: "border-l-[3px] border-l-teal-600", iconColor: "text-teal-700" },
  { titleKey: "dashboard.seizedHps", icon: AlertTriangle, id: "seized-hps", accentTop: "border-l-[3px] border-l-rose-600", iconColor: "text-rose-600" },
] as const;

export function Dashboard({
  setActiveItem,
}: {
  setActiveItem: (item: string) => void;
}) {
  const { t } = useTranslation();
  const chart = useChartTheme();
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const dr = useDateRange("all");
  const customers = useUnifiedCustomers();
  const admin = useAdminEmiPayments();
  const [books] = useBooksOpening();
  const [dataTick, setDataTick] = useState(0);
  useEffect(() => {
    const fn = () => setDataTick((x) => x + 1);
    window.addEventListener(CONSULTANCY_DATA_EVENT, fn);
    window.addEventListener(WORKSPACE_DATA_EVENT, fn);
    window.addEventListener(ACCOUNTING_DATA_EVENT, fn);
    return () => {
      window.removeEventListener(CONSULTANCY_DATA_EVENT, fn);
      window.removeEventListener(WORKSPACE_DATA_EVENT, fn);
      window.removeEventListener(ACCOUNTING_DATA_EVENT, fn);
    };
  }, []);

  // EMI cash collected in the selected window (same formula as Master Dashboard).
  const collectedInRange = useMemo(() => {
    void dataTick;
    return computeFinancePeriodMetrics(admin, dr.range).emiCollected;
  }, [admin, dr.range, dataTick]);

  const paymentFlows = useMemo(() => {
    void dataTick;
    return computePaymentFlows(dr.range, admin);
  }, [dr.range, dataTick, admin]);

  /** Live balances: opening (Accounts) + cash in − cash out since cutover. */
  const liveCashBank = useMemo(() => {
    void dataTick;
    return computeLiveCashBank(books, admin);
  }, [books, admin, dataTick]);

  const statOverrides = useMemo(() => {
    void dataTick;
    const portfolio = computePortfolioTotals(customers);
    const bundle = readConsultancyBundle();
    const avail = bundle.vehicles.filter((v) => v.status === "available").length;
    const sold = bundle.vehicles.filter((v) => v.status === "sold").length;
    const principalDisplay =
      portfolio.principal > 0 ? portfolio.principal : Math.max(0, Number(books.balances.outstanding) || 0);
    const outstandingDisplay =
      portfolio.outstanding > 0 ? portfolio.outstanding : principalDisplay;
    return {
      "total-users": String(portfolio.borrowers),
      "collected-emis": formatInrCompact(collectedInRange),
      "total-loan": formatInrCompact(principalDisplay),
      "hand-loans": formatInrCompact(portfolio.handPrincipal),
      "closed-hps": String(countFullyClosedCustomers(customers)),
      "vehicles-available": String(avail),
      "vehicles-sold": String(sold),
      "seized-hps": String(readSeizedFinanceRecords().length),
      outstanding: formatInrCompact(outstandingDisplay),
    } as Record<string, string>;
  }, [customers, collectedInRange, dataTick, books.balances.outstanding]);

  const statsCards = useMemo(
    () =>
      statsCardDefs.map((card) => ({
        ...card,
        title: t(card.titleKey),
        value: "0",
        change: "—",
      })),
    [t]
  );

  // Monthly collections trend from the admin ledger (respects date range).
  const monthlyCollections = useMemo(() => {
    void dataTick;
    const map = new Map<string, { emi: number; late: number }>();
    for (const p of admin) {
      if (!inRange(p.paidAt, dr.range)) continue;
      const ym = String(p.paidAt).slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(ym)) continue;
      const o = map.get(ym) ?? { emi: 0, late: 0 };
      o.emi += emiCashFromPayment(p);
      o.late += lateFeeCashFromPayment(p);
      map.set(ym, o);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([ym, v]) => ({ month: ym, collected: Math.round(v.emi), late: Math.round(v.late) }));
  }, [admin, dr.range, dataTick]);

  // Portfolio split: collected vs outstanding (current snapshot — same as Customers list).
  const portfolioSplit = useMemo(() => {
    const portfolio = computePortfolioTotals(customers);
    return [
      { name: t('common.collected'), value: portfolio.collected },
      { name: t('common.outstanding'), value: portfolio.outstanding },
    ];
  }, [customers, t]);

  // Top borrowers by outstanding balance.
  const topOutstanding = useMemo(
    () =>
      [...customers]
        .sort((a, b) => b.pendingLoan - a.pendingLoan)
        .slice(0, 6)
        .map((c) => ({ name: c.name.split(" ")[0] || c.name, outstanding: Math.round(c.pendingLoan) })),
    [customers],
  );

  const overviewExportRows: ExportRow[] = useMemo(
    () => [
      ...statsCards.map((card) => ({
        Metric: card.title,
        Value: statOverrides[card.id] ?? card.value,
      })),
      { Metric: t('payment.cashTaken'), Value: formatInrCompact(paymentFlows.cashTaken) },
      { Metric: t('payment.cashGiven'), Value: formatInrCompact(paymentFlows.cashGiven) },
      { Metric: t('payment.accountCredited'), Value: formatInrCompact(paymentFlows.bankCredited) },
      { Metric: t('payment.accountDebited'), Value: formatInrCompact(paymentFlows.bankDebited) },
    ],
    [statOverrides, statsCards, paymentFlows, t],
  );

  const pieColors = useMemo(() => [...chart.piePair], [chart.piePair]);

  const renderCardContent = () => {
    switch (selectedCard) {
      case "Active-users":
        return <ActiveUsers onClose={() => setSelectedCard(null)} />;
      case "total-users":
        return <UserList onClose={() => setSelectedCard(null)} setActiveItem={setActiveItem} />;
      case "collected-emis":
        return <CollectedEMIs onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      case "closed-hps":
        return <ClosedHPs onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      case "total-loan":
        return <TotalLoanAmount onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      case "vehicles-available":
        return <AvailableVehicles onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      case "vehicles-sold":
        return <SoldVehicles onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      case "hand-loans":
        return (
          <HandLoansOverview
            onClose={() => setSelectedCard(null)}
            setActiveItem={setActiveItem}
            initialDateRange={dr.range}
          />
        );
      case "seized-hps":
        return <SeizedHPs onClose={() => setSelectedCard(null)} initialDateRange={dr.range} />;
      default:
        return null;
    }
  };

  if (selectedCard) {
    return renderCardContent();
  }

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800 app-theme-accent-text">
            {t('nav.billingCollections')}
          </p>
          <h1 className="app-page-title mt-0.5">{t('dashboard.overview')}</h1>
          <p className="app-page-sub">
            {t('dashboard.overviewSub', { range: formatRangeLabel(dr.range) })}
          </p>
        </div>
        <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <DateRangeFilter
            start={dr.range.start}
            end={dr.range.end}
            preset={dr.preset}
            onStart={dr.setStart}
            onEnd={dr.setEnd}
            onPreset={dr.setPreset}
            onClear={dr.clear}
            compact
          />
          <ExportMenu
            title={`${t('dashboard.exportReport')} (${formatRangeLabel(dr.range)})`}
            excelFilename="overview-summary.xls"
            rows={overviewExportRows}
          />
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
        {statsCards.map((card) => (
          <DashboardCard
            key={card.id}
            dense
            title={card.title}
            value={statOverrides[card.id] ?? card.value}
            change={card.change}
            icon={card.icon}
            accentTop={card.accentTop}
            iconColor={card.iconColor}
            onClick={() => setSelectedCard(card.id)}
          />
        ))}
      </div>

      <section className="space-y-3">
        <div className="border-b border-zinc-200/80 pb-3 dark:border-zinc-700">
          <p className="app-section-label">{t('nav.billingCollections')}</p>
          <h2 className="app-section-heading mt-0.5">{t('dashboard.cashBankFlows')}</h2>
          <p className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            {t('dashboard.cashBankFlowsSub', { range: formatRangeLabel(dr.range) })}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
          <DashboardCard
            dense
            title={t('master.openingCash')}
            value={formatInrCompact(liveCashBank.cash)}
            hint={t('master.liveCashHint')}
            icon={Wallet}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => setActiveItem('accounts')}
          />
          <DashboardCard
            dense
            title={t('master.openingBank')}
            value={formatInrCompact(liveCashBank.bank)}
            hint={t('master.liveBankHint')}
            icon={Landmark}
            accentTop="border-l-[3px] border-l-sky-600"
            iconColor="text-sky-700"
            onClick={() => setActiveItem('accounts')}
          />
          <DashboardCard
            dense
            title={t('common.outstanding')}
            value={statOverrides.outstanding ?? '₹0'}
            hint={t('master.portfolioHint')}
            icon={AlertTriangle}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => setActiveItem('customers')}
          />
          <DashboardCard
            dense
            title={t('payment.cashTaken')}
            value={formatInrCompact(paymentFlows.cashTaken)}
            hint={t('payment.collectionsIn')}
            icon={ArrowDownLeft}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => {}}
          />
          <DashboardCard
            dense
            title={t('payment.cashGiven')}
            value={formatInrCompact(paymentFlows.cashGiven)}
            hint={t('payment.loanDisbursed')}
            icon={ArrowUpRight}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => {}}
          />
          <DashboardCard
            dense
            title={t('payment.accountCredited')}
            value={formatInrCompact(paymentFlows.bankCredited)}
            hint={t('payment.collectionsIn')}
            icon={Landmark}
            accentTop="border-l-[3px] border-l-sky-600"
            iconColor="text-sky-700"
            onClick={() => {}}
          />
          <DashboardCard
            dense
            title={t('payment.accountDebited')}
            value={formatInrCompact(paymentFlows.bankDebited)}
            hint={t('payment.loanDisbursed')}
            icon={Building2}
            accentTop="border-l-[3px] border-l-indigo-600"
            iconColor="text-indigo-700"
            onClick={() => {}}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="app-card app-chart-wrap lg:col-span-2">
          <div className="app-card-inner">
            <div className="mb-4 flex items-center justify-between border-b border-zinc-100 pb-3 dark:border-zinc-800">
              <div>
                <p className="app-section-label">{t('dashboard.overview')}</p>
                <h2 className="app-section-heading mt-0.5 app-theme-heading">{t('dashboard.collectionsTrend')}</h2>
                <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('dashboard.collectionsTrendSub', { range: formatRangeLabel(dr.range) })}</p>
              </div>
            </div>
            {monthlyCollections.length === 0 ? (
              <p className="py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">{t('dashboard.noData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={monthlyCollections} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={chart.areaGradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chart.gradientStart} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={chart.gradientStart} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke={chart.axis} />
                  <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} tickFormatter={(v) => formatInrCompact(Number(v))} />
                  <Tooltip formatter={(v: number) => `₹${Number(v).toLocaleString("en-IN")}`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="collected" stroke={chart.primaryLight} strokeWidth={2} fill={`url(#${chart.areaGradientId})`} name={t('dashboard.collected')} />
                  <Area type="monotone" dataKey="late" stroke={chart.warning} strokeWidth={2} fillOpacity={0} name={t('reports.lateFees')} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="app-card app-chart-wrap">
          <div className="app-card-inner">
            <p className="app-section-label">{t('common.finance')}</p>
            <h2 className="app-section-heading mt-0.5">{t('dashboard.portfolioSplit')}</h2>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('common.collected')} vs {t('common.outstanding')}</p>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={portfolioSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {portfolioSplit.map((entry, i) => (
                    <Cell key={entry.name} fill={pieColors[i % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="app-card">
        <div className="app-card-inner">
          <p className="app-section-label">{t('nav.customers')}</p>
          <h2 className="app-section-heading mt-0.5">{t('dashboard.topBorrowers')}</h2>
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('common.finance')} &amp; {t('common.handLoans')}</p>
          {topOutstanding.length === 0 ? (
            <p className="py-12 text-center text-sm text-zinc-500">{t('dashboard.noData')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topOutstanding} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
                <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} tickFormatter={(v) => formatInrCompact(Number(v))} />
                <Tooltip formatter={(v: number) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Bar dataKey="outstanding" fill={chart.primary} radius={[4, 4, 0, 0]} name={t('common.outstanding')} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="app-card overflow-hidden">
        <div className="grid grid-cols-1 divide-y divide-zinc-200/80 dark:divide-zinc-700 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x sm:divide-y-0">
          <div className="bg-gradient-to-br from-zinc-50 to-emerald-50/30 px-5 py-5 dark:from-zinc-800/80 dark:to-emerald-950/20 lg:col-span-1">
            <p className="app-section-label">{t('dashboard.quickActions')}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">Origination, inventory desk, and loans in one strip.</p>
          </div>
          <button
            type="button"
            onClick={() => setActiveItem("finance")}
            className="group flex items-center justify-between px-5 py-5 text-left text-sm font-semibold text-zinc-900 transition-colors hover:bg-emerald-50/70 dark:text-zinc-100 dark:hover:bg-emerald-950/30"
          >
            {t('dashboard.newFinance')}
            <ArrowRight className="h-4 w-4 text-emerald-700 transition-transform group-hover:translate-x-0.5 dark:text-emerald-400" />
          </button>
          <button
            type="button"
            onClick={() => setActiveItem("loans")}
            className="group flex items-center justify-between px-5 py-5 text-left text-sm font-semibold text-zinc-900 transition-colors hover:bg-emerald-50/70 dark:text-zinc-100 dark:hover:bg-emerald-950/30"
          >
            {t('dashboard.newHandLoan')}
            <ArrowRight className="h-4 w-4 text-emerald-700 transition-transform group-hover:translate-x-0.5 dark:text-emerald-400" />
          </button>
          <button
            type="button"
            onClick={() => setActiveItem("consultancy")}
            className="group flex items-center justify-between px-5 py-5 text-left text-sm font-semibold text-zinc-900 transition-colors hover:bg-emerald-50/70 dark:text-zinc-100 dark:hover:bg-emerald-950/30"
          >
            <span className="flex items-center gap-2">
              <Users2 className="h-4 w-4 text-emerald-700 dark:text-emerald-400" aria-hidden />
              {t('dashboard.consultancyDesk')}
            </span>
            <ArrowRight className="h-4 w-4 text-emerald-700 transition-transform group-hover:translate-x-0.5 dark:text-emerald-400" />
          </button>
        </div>
      </div>

    </div>
  );
}
